/**
 * Checks if there is severe hearing loss based on the device and audiograms as input
 *
 * @param deviceModelId Input model id for the hearing device
 * @param audiograms Input binaural audiograms for the hearing device
 * @returns Returns whether there is a severe hearing loss
 */
export function severeHearingLossCheck(deviceModelId, audiograms) {
    var C10SevereHearingLossCriteria = {
        Criteria1Max: 4,
        Criteria2Max: 2,
        Criteria3Max: 3,
        freq1Threshold: 40,
        freq2Threshold: 55,
        freq3Threshold: 70,
        freq4Threshold: 75,
        maxThreshold: 85,
    };
    var E10SevereHearingLossCriteria = {
        Criteria1Max: 4,
        Criteria2Max: 2,
        Criteria3Max: 3,
        freq1Threshold: 60,
        freq2Threshold: 65,
        freq3Threshold: 75,
        freq4Threshold: 80,
        maxThreshold: 85,
    };
    var C20SevereHearingLossCriteria = {
        Criteria1Max: 4,
        Criteria2Max: 2,
        Criteria3Max: 3,
        freq1Threshold: 60,
        freq2Threshold: 65,
        freq3Threshold: 75,
        freq4Threshold: 80,
        maxThreshold: 85,
    };
    var T100SevereHearingLossCriteria = {
        Criteria1Max: 4,
        Criteria2Max: 2,
        Criteria3Max: 3,
        freq1Threshold: 60,
        freq2Threshold: 65,
        freq3Threshold: 75,
        freq4Threshold: 80,
        maxThreshold: 85,
    };
    var severeHearingLossCriteria = C10SevereHearingLossCriteria;
    switch (deviceModelId) {
        case "6144007":
            severeHearingLossCriteria = C10SevereHearingLossCriteria;
            break;
        case "540938247":
            severeHearingLossCriteria = E10SevereHearingLossCriteria;
            break;
        case "72780372515670":
            severeHearingLossCriteria = C20SevereHearingLossCriteria;
            break;
        case "72986808818502":
            severeHearingLossCriteria = T100SevereHearingLossCriteria;
            break;
    }
    return isSevereHearingLoss(severeHearingLossCriteria, audiograms);
}
/**
 * Checks if there is severe hearing loss based on the criteria as input
 *
 * @param severeHearingLossCriteria Input severe hearing loss criteria
 * @param audiograms Input binaural audiograms for the hearing device
 * @returns Returns whether there is a severe hearing loss
 */
export function isSevereHearingLoss(severeHearingLossCriteria, audiograms) {
    var Criteria1Max = severeHearingLossCriteria.Criteria1Max, Criteria2Max = severeHearingLossCriteria.Criteria2Max, Criteria3Max = severeHearingLossCriteria.Criteria3Max;
    var exclusion1 = 0;
    var exclusion2 = 0;
    var Sides = Object.keys(audiograms);
    Sides.forEach(function (side) {
        // exclusion 3 is counted per side
        var exclusion3 = 0;
        var thresholds = audiograms[side];
        exclusion1 += calculateExclusion1(thresholds, severeHearingLossCriteria);
        exclusion2 += calculateExclusion2(thresholds, severeHearingLossCriteria);
        exclusion3 += calculateExclusion3(thresholds, severeHearingLossCriteria);
        if (exclusion3 >= Criteria3Max) {
            return true;
        }
    });
    return (exclusion1 >= Criteria1Max || exclusion2 >= Criteria2Max);
}
function calculateExclusion1(thresholds, severeHearingLossCriteria) {
    var thresholdAtfreq1 = thresholds[0], thresholdAtfreq2 = thresholds[1], thresholdAtfreq3 = thresholds[2], thresholdAtfreq4 = thresholds[3];
    var freq1Threshold = severeHearingLossCriteria.freq1Threshold, freq2Threshold = severeHearingLossCriteria.freq2Threshold, freq3Threshold = severeHearingLossCriteria.freq3Threshold, freq4Threshold = severeHearingLossCriteria.freq4Threshold;
    var exclusionCount = 0;
    if (thresholdAtfreq1 > freq1Threshold)
        exclusionCount++;
    if (thresholdAtfreq2 > freq2Threshold)
        exclusionCount++;
    if (thresholdAtfreq3 > freq3Threshold)
        exclusionCount++;
    if (thresholdAtfreq4 > freq4Threshold)
        exclusionCount++;
    return exclusionCount;
}
function calculateExclusion2(thresholds, severeHearingLossCriteria) {
    var thresholdAtfreq1 = thresholds[0], thresholdAtfreq2 = thresholds[1];
    var maxThreshold = severeHearingLossCriteria.maxThreshold;
    var exclusionCount = 0;
    if (thresholdAtfreq1 >= maxThreshold) {
        exclusionCount++;
    }
    if (thresholdAtfreq2 >= maxThreshold) {
        exclusionCount++;
    }
    return exclusionCount;
}
function calculateExclusion3(thresholds, severeHearingLossCriteria) {
    var thresholdAtfreq1 = thresholds[0], thresholdAtfreq2 = thresholds[1], thresholdAtfreq3 = thresholds[2], thresholdAtfreq4 = thresholds[3];
    var maxThreshold = severeHearingLossCriteria.maxThreshold;
    var exclusionCount = 0;
    if (thresholdAtfreq1 >= maxThreshold) {
        exclusionCount++;
    }
    if (thresholdAtfreq2 >= maxThreshold) {
        exclusionCount++;
    }
    if (thresholdAtfreq3 >= maxThreshold) {
        exclusionCount++;
    }
    if (thresholdAtfreq4 >= maxThreshold) {
        exclusionCount++;
    }
    return exclusionCount;
}
